<script setup lang="ts">
import {ref} from "vue";
import {getCsrfParam, getCsrfToken} from "../ts/utils";

const props = defineProps<{
  uploadId: number;
  uploadUrl: string;
  formName: string;
  minImageWidth: number;
  minImageHeight: number;
}>()

const uploadId = ref<number>(props.uploadId)
const previewUrl = ref<string>(props.uploadUrl)
const busy = ref(false)
const error = ref("")
const fileInput = ref<HTMLInputElement | null>(null)

async function uploadFile() {
  const file = fileInput.value?.files?.[0]
  if (!file) return

  busy.value = true
  error.value = ""
  const form = new FormData()
  form.append('file', file)
  form.append(getCsrfParam(), getCsrfToken())

  try {
    const response = await fetch('/site/upload', {method: 'POST', body: form})
    const result = await response.json()
    if (!response.ok || !result.success) {
      error.value = Object.values(result.errors || {}).join(' ') || 'Не удалось загрузить изображение.'
      return
    }
    uploadId.value = result.model.id
    previewUrl.value = result.model.url
  } catch {
    error.value = 'Не удалось загрузить изображение. Попробуйте ещё раз.'
  } finally {
    busy.value = false
    if (fileInput.value) fileInput.value.value = ''
  }
}
</script>

<template>
  <div>
    <input
      ref="fileInput"
      accept="image/png,image/jpeg"
      class="sr-only"
      type="file"
      @change="uploadFile"
    >
    <input :name="formName + '[upload_id]'" type="hidden" :value="uploadId || ''">
    <div class="flex flex-wrap items-center gap-3">
      <button
        type="button"
        class="app-button app-button-secondary"
        :disabled="busy"
        @click="fileInput?.click()"
      >
        {{ busy ? 'Загрузка…' : uploadId ? 'Выбрать другое изображение' : 'Выбрать изображение' }}
      </button>
      <span v-if="uploadId" class="text-sm text-green-700" role="status">Изображение загружено</span>
    </div>
    <div class="mt-2 space-y-1 text-sm text-slate-500">
      <p>Минимальный размер изображения — {{ minImageWidth }} × {{ minImageHeight }} пикселя.</p>
      <p>Поддерживаемые форматы: PNG и JPEG.</p>
    </div>
    <img
      v-if="previewUrl"
      :src="previewUrl"
      alt="Предпросмотр загруженного фона сертификата"
      class="mt-4 max-h-80 rounded-md border border-slate-200 bg-slate-50 object-contain"
    >
    <p v-if="error" class="mt-2 text-sm text-red-600" role="alert">{{ error }}</p>
  </div>
</template>
